import { z } from "zod";
import { RUN_STATUSES } from "@/features/payroll/payroll-run-vocabulary";
import { payrollCorrectionStatusSchema } from "@/features/payroll/schemas/payroll-correction.schema";
import { payrollObligationStatusSchema } from "@/features/payroll/schemas/payroll-obligation.schema";
import { resolvePayrollRunsView } from "@/shared/components/layout/payroll-sidebar-model";

/// URL search params for `/$workspaceSlug/payroll/runs`.
///
/// Both live in the URL rather than `useState` (S3): "show me the runs that
/// need attention" is a link an admin sends a colleague, and a refresh in the
/// middle of a payroll afternoon should not throw the filter away.
///
/// An unknown `status` is normalized to undefined rather than refused: a stale
/// bookmark should land somebody on the run list, not on an error about a word.
export const payrollRunsRouteSearchSchema = z
  .object({
    view: z.enum(["corrections", "days"]).optional().catch(undefined),
    status: z.enum(RUN_STATUSES).optional().catch(undefined),
    q: z.string().optional().catch(undefined),
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    correctionStatus: payrollCorrectionStatusSchema.optional().catch(undefined),
    correctionId: z.string().min(1).optional().catch(undefined),
    correctionCursor: z.string().min(1).optional().catch(undefined),
    obligationStatus: payrollObligationStatusSchema.optional().catch(undefined),
    obligationCursor: z.string().min(1).optional().catch(undefined),
  })
  .strict();

export type PayrollRunsRouteSearch = z.infer<typeof payrollRunsRouteSearchSchema>;

/** Older notifications predate the focused views; their targets must still open. */
export function payrollRunsView(search: PayrollRunsRouteSearch): "runs" | "corrections" | "days" {
  return resolvePayrollRunsView(search);
}

export function payrollRunsLandingSearch(search: PayrollRunsRouteSearch): PayrollRunsRouteSearch {
  return { q: search.q, status: search.status, ...(search.month ? { month: search.month } : {}) };
}
